import { css } from '@linaria/core';

import { createThemedCssVarDefinitions, pxToRem } from '@creditornot/cb-utils';

import { colors } from './colors';

export type ElevationLevel = 'xsmall' | 'small' | 'medium' | 'large';

const elevationXsmall: string = css`
  @layer cb {
    background-color: var(--cb-color-bg);
    box-shadow: var(--cb-shadow-xsmall);
  }
`;

const elevationSmall: string = css`
  @layer cb {
    background-color: var(--cb-color-bg);
    box-shadow: var(--cb-shadow-small);
  }
`;

const elevationMedium: string = css`
  @layer cb {
    background-color: var(--cb-color-bg);
    box-shadow: var(--cb-shadow-medium);
  }
`;

const elevationLarge: string = css`
  @layer cb {
    background-color: var(--cb-color-bg);
    box-shadow: var(--cb-shadow-large);
  }
`;

export const ELEVATED_CONTAINER_CLASSNAME = 'cb-elevated' as const;

export const elevationClassName: Record<ElevationLevel, string> = {
  xsmall: `${ELEVATED_CONTAINER_CLASSNAME} ${elevationXsmall}`,
  small: `${ELEVATED_CONTAINER_CLASSNAME} ${elevationSmall}`,
  medium: `${ELEVATED_CONTAINER_CLASSNAME} ${elevationMedium}`,
  large: `${ELEVATED_CONTAINER_CLASSNAME} ${elevationLarge}`,
};

/**
 * @deprecated Use `elevationClassName` instead to implement elevation.
 */
export type LegacyElevationLevel = 0 | 1 | 2 | 3 | 4 | 6 | 8 | 12 | 16 | 24;

interface ElevationConfiguration {
  dark: {
    backgroundElevation: LegacyElevationLevel;
    shadowElevation: LegacyElevationLevel;
  };
  light: LegacyElevationLevel;
}

/**
 * @deprecated Use `elevationClassName` instead to implement elevation.
 */
export const darkModeElevationBGColors: Record<LegacyElevationLevel, string> = {
  0: '#171717',
  1: '#181818',
  2: '#191919',
  3: '#191919',
  4: '#1C1C1C',
  6: '#1F1F1F',
  8: '#212121',
  12: '#242424',
  16: '#262626',
  24: '#303030',
};

Object.freeze(darkModeElevationBGColors);

/**
 * Sets `background-color` and `box-shadow` based on the elevation level.
 *
 * @deprecated Use `elevationClassName` instead to implement elevation.
 */
export const elevationCss = (level: LegacyElevationLevel | ElevationConfiguration) => {
  const backgroundElevation =
    typeof level === 'object'
      ? {
          light: level.light,
          dark: level.dark.backgroundElevation,
        }
      : { light: level, dark: level };

  const shadowElevation =
    typeof level === 'object'
      ? {
          light: level.light,
          dark: level.dark.shadowElevation,
        }
      : { light: level, dark: level };

  const bgCss = `
    ${createThemedCssVarDefinitions({
      backgroundElevated: {
        light: colors.salt100,
        lightHighContrast: colors.salt100,
        dark: darkModeElevationBGColors[backgroundElevation.dark],
      },
    })}

    background-color: var(--backgroundElevated);
  `;

  if (shadowElevation.light === 0 && shadowElevation.dark === 0) {
    return bgCss;
  }

  return `
    ${bgCss}

    ${createThemedCssVarDefinitions({
      boxShadowElevated: {
        light: `0 0 ${pxToRem(shadowElevation.light)} 0 rgba(0, 0, 0, 0.06),
          0 ${pxToRem(shadowElevation.light)} ${pxToRem(
            shadowElevation.light,
          )} 0 rgba(0, 0, 0, 0.12)`,
        lightHighContrast: `0 0 ${pxToRem(shadowElevation.light)} 0 rgba(0, 0, 0, 0.8),
          0 ${pxToRem(shadowElevation.light)} ${pxToRem(
            shadowElevation.light,
          )} 0 rgba(0, 0, 0, 0.12)`,
        dark: `0 0 ${pxToRem(shadowElevation.dark)} 0 rgba(0, 0, 0, 0.06),
          0 ${pxToRem(shadowElevation.dark)} ${pxToRem(
            shadowElevation.dark,
          )} 0 rgba(0, 0, 0, 0.12)`,
      },
    })}

    box-shadow: var(--boxShadowElevated);
  `;
};
